import type { Features, FeatureKey } from "@baton/api";
import type { ReactNode } from "react";

/**
 * FR-015-002, FR-015-071, § Feedback and status: `FeatureGate` renders children or nothing
 * and never a disabled placeholder; `PermissionGate` renders children, nothing, or the
 * no-access panel the host supplies. Neither is an authorisation decision: the server
 * decides every time (`contracts/client-api.md` § The permission mirror). Neither draws anything
 * of its own, so neither has a Spectrum UI or shadcn/ui source to take (2026-09-18, slice (f)).
 */
export interface FeatureGateProps {
  readonly features: Features;
  readonly feature: FeatureKey;
  readonly children: ReactNode;
}

export function isFeatureAvailable(features: Features, feature: FeatureKey): boolean {
  return features[feature] === true;
}

export function FeatureGate({ features, feature, children }: FeatureGateProps) {
  return isFeatureAvailable(features, feature) ? <>{children}</> : null;
}

export interface PermissionGateProps {
  /** Whether the mirror has recorded a refusal for this operation here. */
  readonly refused: boolean;
  /** What to render for a refused route: the no-access panel. Omitted, the gate renders nothing. */
  readonly fallback?: ReactNode;
  readonly children: ReactNode;
}

export function PermissionGate({ refused, fallback, children }: PermissionGateProps) {
  if (!refused) return <>{children}</>;
  return fallback === undefined ? null : <>{fallback}</>;
}
